import { maybe, mt, MutationData, rd, RemoteData } from '@passionware/monads';
import { VariantProps } from 'class-variance-authority';
import { AlertTriangle, Check, CornerDownLeft, Meh, Plus } from 'lucide-react';
import React, {
  ComponentProps,
  createContext,
  forwardRef,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { DimmedContainer } from '../../v4/shared/components/forms/input/primitvies/DimmedContainer';
import { cn } from '../platform/dom/cn';
import { useControllableState } from '../platform/react/useControllableState';
import { Overwrite } from '../platform/typescript';
import { ModifierKey } from '../primitives/ModifierKey';
import { Button } from './Button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
  CommandProps,
  CommandSeparator
} from './Command';
import { DropdownMenuButtonArrow } from './DropdownMenu';
import { Kbd } from './Kbd';
import { LoadingSpinner } from './LoadingSpinner';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import { selectTriggerVariants } from './Select';

export interface ComboboxProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
  disabled?: boolean;
  /**
   * When set, opening the popover sets the search input to this string (e.g. current value).
   * Closing still clears the query. Omit to keep the default empty search on open.
   */
  defaultQuery?: string;
}

interface ComboboxContext<ComboboxValueBase> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  query: string;
  onQueryChange: (query: string) => void;
}

const ComboboxContext = createContext<ComboboxContext<any> | null>(null);

export function Combobox({
  open: openProp,
  onOpenChange,
  children,
  defaultQuery
}: ComboboxProps) {
  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChange,
    fallbackValue: false
  });
  const [query, setQuery] = useState('');

  const ctx = useMemo(
    () => ({
      open,
      onOpenChange: setOpen,
      query,
      onQueryChange: setQuery
    }),
    [open, setOpen, query, setQuery]
  );

  return (
    <ComboboxContext.Provider value={ctx}>
      <Popover
        open={open}
        onOpenChange={nextOpen => {
          setOpen(nextOpen);
          if (!nextOpen) {
            // we are about to close the popover, clear the query
            setQuery('');
          } else if (defaultQuery !== undefined) {
            // we are about to open the popover, set the query to the default query if provided
            setQuery(defaultQuery);
          }
        }}
      >
        {children}
      </Popover>
    </ComboboxContext.Provider>
  );
}

/**
 * Use this if you don't want default trigger-popover behavior.
 * You can put this into dropdown menu sub content
 */
export function InlineCombobox({
  children,
  size = 'md',
  value,
  onValueChange
}: {
  children: ReactNode;
  size?: CommandProps['size'];
  /** The highlighted item, when the host tracks it (as the popover's `value`). */
  value?: CommandProps['value'];
  onValueChange?: CommandProps['onValueChange'];
}) {
  const [open, setOpen] = useState(true); // Always open when used in dropdowns
  const [query, setQuery] = useState('');

  const ctx = useMemo(
    () => ({
      open,
      onOpenChange: setOpen,
      query,
      onQueryChange: setQuery
    }),
    [open, setOpen, query, setQuery]
  );

  return (
    <ComboboxContext.Provider value={ctx}>
      <Command
        size={size}
        shouldFilter={false}
        value={value}
        onValueChange={onValueChange}
      >
        {children}
      </Command>
    </ComboboxContext.Provider>
  );
}

function useComboboxContext() {
  return maybe.getOrThrow(
    useContext(ComboboxContext),
    'useComboboxQuery must be used within a Combobox'
  );
}

/**
 * Public hook designed to be used to read combobox search query value.
 */
export const useComboboxQuery = () => useComboboxContext().query;

/**
 * Public render-props component designed to be used to read combobox search query value.
 * It is handling loading and error states.
 * @param props
 * @constructor
 */
export const ComboboxQueryRenderer = (props: {
  children: (query: string, isOpen: boolean) => RemoteData<ReactNode>;
  emptyContent?: ReactNode;
}) => {
  const ctx = useComboboxContext();
  const remote = props.children(ctx.query, ctx.open);
  return (
    <ComboboxDataRenderer emptyContent={props.emptyContent}>
      {remote}
    </ComboboxDataRenderer>
  );
};

export const ComboboxDataRenderer = (props: {
  children: RemoteData<ReactNode>;
  emptyContent?: ReactNode;
}) => {
  const remote = props.children;
  return (
    <DimmedContainer shouldDim={rd.isPlaceholderData(remote)}>
      {rd
        .journey(remote)
        .wait(<ComboboxLoading />)
        .catch(e => {
          console.error('Combobox error', e);
          return <ComboboxError />;
        })
        .map(x => (
          <>
            <ComboboxEmpty>{props.emptyContent}</ComboboxEmpty>
            {x}
          </>
        ))}
    </DimmedContainer>
  );
};

export function isComboboxTriggerArrowDownKey(event: {
  key: string;
  metaKey?: boolean;
  ctrlKey?: boolean;
  altKey?: boolean;
}): boolean {
  return (
    event.key === 'ArrowDown' &&
    event.metaKey !== true &&
    event.ctrlKey !== true &&
    event.altKey !== true
  );
}

export const ComboboxTrigger = forwardRef<
  React.ElementRef<typeof PopoverTrigger>,
  ComponentProps<typeof PopoverTrigger>
>(({ onKeyDown, ...props }, ref) => {
  const { open, onOpenChange } = useComboboxContext();

  return (
    <PopoverTrigger
      ref={ref}
      {...props}
      onKeyDown={e => {
        onKeyDown?.(e);
        if (e.defaultPrevented || open) {
          return;
        }
        if (isComboboxTriggerArrowDownKey(e)) {
          e.preventDefault();
          e.stopPropagation();
          onOpenChange(true);
        }
      }}
    />
  );
});
ComboboxTrigger.displayName = 'ComboboxTrigger';

export const ComboboxContent = forwardRef<
  HTMLDivElement,
  ComponentProps<typeof PopoverContent> & {
    size?: CommandProps['size'];
    shouldFilter?: boolean;
  } & Pick<CommandProps, 'value' | 'onValueChange' | 'loop' | 'onKeyDown'>
>(
  (
    {
      align = 'start',
      size = 'md',
      shouldFilter = false,
      value,
      onValueChange,
      loop,
      onKeyDown,
      children,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <PopoverContent ref={ref} align={align} {...props} asChild>
        <Command
          className={cn('p-0', className)}
          size={size}
          shouldFilter={shouldFilter}
          value={value}
          onValueChange={onValueChange}
          loop={loop}
          onKeyDown={onKeyDown}
        >
          {children}
        </Command>
      </PopoverContent>
    );
  }
);

export const ComboboxEmpty = forwardRef<
  React.ElementRef<typeof CommandEmpty>,
  ComponentProps<typeof CommandEmpty>
>(({ children, ...rest }, ref) => (
  <CommandEmpty ref={ref} {...rest}>
    {children ?? (
      <>
        <Meh className="size-5" />
        No results
      </>
    )}
  </CommandEmpty>
));

export const ComboboxLoading = CommandLoading;
export const ComboboxList = CommandList;
export const ComboboxGroup = forwardRef<
  React.ElementRef<typeof CommandGroup>,
  ComponentProps<typeof CommandGroup>
>(({ className, ...props }, ref) => (
  <CommandGroup
    ref={ref}
    className={cn('has-[[cmdk-group-items]:empty]:hidden', className)}
    {...props}
  />
));

export const ComboboxInput = forwardRef<
  React.ElementRef<typeof CommandInput>,
  ComponentProps<typeof CommandInput> & {
    /** Off where the combobox sits open on a page beside other fields. */
    focusOnOpen?: boolean;
  }
>(({ placeholder = 'Search...', focusOnOpen = true, ...props }, ref) => {
  const ctx = useComboboxContext();

  // Focus the input when the combobox opens
  useEffect(() => {
    if (ctx.open && focusOnOpen) {
      // Small delay to ensure Radix UI has finished its focus management
      const timer = setTimeout(() => {
        const input = document.querySelector(
          '[cmdk-input]'
        ) as HTMLInputElement;

        input?.focus();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [ctx.open, focusOnOpen]);

  return (
    <CommandInput
      ref={ref}
      value={ctx.query}
      onValueChange={ctx.onQueryChange}
      placeholder={placeholder}
      variant="combobox"
      {...props}
    />
  );
});

export const ComboboxError = forwardRef<
  React.ElementRef<typeof CommandItem>,
  ComponentProps<typeof CommandItem>
>(({ className, children, ...rest }, ref) => (
  <ComboboxGroup>
    <ComboboxItem
      disabled
      ref={ref}
      className={cn('text-fg-danger', className)}
      mutation={mt.ofError(void 0, new Error(''))}
      {...rest}
    >
      {children ?? 'Something went wrong'}
    </ComboboxItem>
  </ComboboxGroup>
));

export const ComboboxItem = forwardRef<
  React.ElementRef<typeof CommandItem>,
  Overwrite<
    ComponentProps<typeof CommandItem>,
    {
      isSelected?: boolean;
      closeOnSelect?: boolean;
      mutation?: MutationData<unknown, unknown>;
      onSelect?: (value: string) => void | Promise<void>;
      // if compact - it won't reserve space for mutation or checkmark
      compact?: boolean;
    }
  >
>(
  (
    {
      isSelected,
      mutation = mt.ofIdle(),
      closeOnSelect = true,
      compact = false,
      children,
      ...props
    },
    ref
  ) => {
    const ctx = useComboboxContext();
    const checkmark =
      !compact || isSelected ? (
        <Check
          className={cn('size-4', isSelected ? 'opacity-100' : 'opacity-0')}
        />
      ) : null;
    return (
      <CommandItem
        ref={ref}
        {...props}
        // A label with no spaces still wraps instead of being cut at the edge.
        className={cn('[overflow-wrap:anywhere]', props.className)}
        onSelect={async value => {
          await props.onSelect?.(value);
          ctx.onQueryChange('');
          if (closeOnSelect) {
            ctx.onOpenChange(false);
          }
        }}
      >
        {mt
          .journey(mutation)
          .initially(checkmark)
          .during(<LoadingSpinner className="" />)
          .catch(() => <AlertTriangle className="text-fg-danger" />)
          .done(checkmark)}
        {children}
      </CommandItem>
    );
  }
);

export const ComboboxSeparator = CommandSeparator;

export type ComboboxButtonProps = ComponentProps<'button'> &
  VariantProps<typeof selectTriggerVariants> & { readOnly?: boolean };
export const ComboboxButton = forwardRef<
  React.ElementRef<typeof Button>,
  ComboboxButtonProps
>(({ className, variant, size = 'sm', readOnly, children, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      selectTriggerVariants({ variant, size }),
      readOnly && 'pointer-events-none cursor-default',
      'justify-start text-left',
      className
    )}
    aria-readonly={readOnly}
    tabIndex={readOnly ? -1 : props.tabIndex}
    {...props}
  >
    {children}
    {!readOnly && <DropdownMenuButtonArrow />}
  </button>
));

export const ComboboxCreateItem = forwardRef<
  React.ElementRef<typeof ComboboxItem>,
  ComponentProps<typeof ComboboxItem>
>(
  (
    {
      children = defaultPrompt,
      onSelect,
      className,
      closeOnSelect = true,
      ...props
    },
    ref
  ) => {
    const ctx = useComboboxContext();
    const { query } = ctx;

    const handleSelect = async () => {
      await onSelect?.(ctx.query);
      ctx.onQueryChange('');
      if (closeOnSelect) {
        ctx.onOpenChange(false);
      }
    };

    const handleSelectRef = useRef(handleSelect);
    handleSelectRef.current = handleSelect;

    const [isCmdPressed, setIsCmdPressed] = useState(false);

    useEffect(() => {
      const listener = (e: KeyboardEvent) => {
        // this needs to be captured, useHotkeys doesn't support it, and we need to be before cmdk onSelect is fired by other items
        setIsCmdPressed(e.metaKey || e.ctrlKey);
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && query !== '') {
          e.preventDefault();
          e.stopPropagation();
          handleSelectRef.current();
        }
      };
      const upListener = (e: KeyboardEvent) => {
        setIsCmdPressed(false);
      };
      window.addEventListener('keydown', listener, { capture: true });
      window.addEventListener('keyup', upListener, { capture: true });
      return () => {
        window.removeEventListener('keydown', listener, { capture: true });
        window.removeEventListener('keyup', upListener, { capture: true });
      };
    }, []);

    if (!query) {
      return null;
    }
    return (
      <CommandGroup
        className={cn(
          '[:not(:has([cmdk-group-items]:empty))+&]:border-t border-border-default',
          className
        )}
      >
        <CommandItem
          ref={ref}
          onSelect={handleSelect}
          className={cn('transition-all', isCmdPressed && 'bg-brand-50')}
          {...props}
        >
          <Plus />
          <span>{children}</span>
          <Kbd className="text-xs ml-auto text-brand-700! bg-brand-700/10">
            <ModifierKey className="size-3!" />
            <CornerDownLeft className="size-3!" />
          </Kbd>
        </CommandItem>
      </CommandGroup>
    );
  }
);

export const ComboboxCreateItemQuery = forwardRef<
  React.ElementRef<'span'>,
  ComponentProps<'span'>
>(({ className, ...props }, ref) => {
  const ctx = useComboboxContext();
  const { query } = ctx;

  return (
    <span
      ref={ref}
      className={cn('font-bold text-brand-500', className)}
      {...props}
    >
      {query}
    </span>
  );
});
const defaultPrompt = (
  <>
    Create new: <ComboboxCreateItemQuery />
  </>
);
